import { createApp } from '@backstage/frontend-defaults';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  type SignInPageProps,
} from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import apiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import readmePlugin from '@axis-backstage/plugin-readme/alpha';
// SonarQube — code quality metrics from SonarCloud
import sonarQubePlugin from '@backstage-community/plugin-sonarqube/alpha';
// Grafana — DISABLED for now. Re-enabling needs ALL of:
//   1. yarn workspace app add @backstage-community/plugin-grafana@^0.22.0
//      (the dependency itself must come back — `app.packages: all` discovers
//      plugins from package.json, and config schemas are collected from the
//      dependency tree, so commenting out this import alone does nothing)
//   2. this import + grafanaPlugin in the `features` array below
//   3. a `grafana:` block in app-config.production.yaml — the plugin's
//      config.schema.json declares `required: ["grafana"]`, so without it the
//      app bundle fails schema validation and the backend never starts
//   4. the '/grafana/api' proxy endpoint in app-config.production.yaml
//   5. GRAFANA_URL + GRAFANA_TOKEN in the host .env
// import grafanaPlugin from '@backstage-community/plugin-grafana/alpha';
import techRadarPlugin from '@backstage-community/plugin-tech-radar/alpha';
import homePlugin from '@backstage/plugin-home/alpha';
import { navModule } from './modules/nav';
import { jiraModule } from './modules/jira';
import { techRadarModule } from './modules/techRadar';

// Custom sign-in page: Microsoft Entra ID (Azure AD) OAuth.
// This app uses the new frontend system (@backstage/frontend-defaults), whose
// `createApp` has no `components` option. The equivalent extension point is
// `SignInPageBlueprint`, installed via a frontend module (see
// https://backstage.io/docs/frontend-system/architecture/extension-blueprints).
const signInPageModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: async () => (props: SignInPageProps) =>
          (
            <SignInPage
              {...props}
              auto
              provider={{
                id: 'microsoft-auth-provider',
                title: 'Microsoft',
                message: 'Anmelden mit Entra ID',
                apiRef: microsoftAuthApiRef,
              }}
            />
          ),
      },
    }),
  ],
});

// Plugins are also discoverable via `app.packages: all`, but we list them
// explicitly so their pages/cards are deterministically present (dedup with
// discovery is proven safe here by catalogPlugin). techRadarModule overrides
// techRadarApiRef with the in-repo seed dataset (see ./modules/techRadar).
export default createApp({
  features: [
    catalogPlugin,
    apiDocsPlugin,
    readmePlugin,
    sonarQubePlugin,
    // grafanaPlugin, // disabled for now — see the import comment above
    techRadarPlugin,
    homePlugin,
    navModule,
    signInPageModule,
    jiraModule,
    techRadarModule,
  ],
});

